/**
 * EditConnectorDialog — modify an existing DB/ERP connection credentials.
 *
 * Wraps the same credentials field set used at connect-time via the
 * shared ``findCredentialsStep`` lookup (exported from
 * ``ConnectAccountDialog``). Pre-populates non-secret fields from
 * ``connection.metadata`` / ``connection.config``; secret fields render
 * blank with the placeholder "Lascia vuoto per non aggiornare" so the
 * BE preserves the existing encrypted value.
 *
 * Adds a "Verifica connessione" button that runs ``useTestConnection``
 * inline — the result chip shows latency + version, or surfaces the
 * error via ``InlineErrorBanner``.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import * as React from "react";
import { ConnectorDialogShell } from "../ConnectorDialogShell";

import type { ConnectionSummary } from "@ando/core";
import type { ConnectionTestResponse } from "@ando/core/api";
import {
  type BaseConnection,
  Button,
  type CapabilityWizardSpec,
  type FieldSpec,
  cn,
  localize,
  localizeOpt,
} from "@ando/ui";

import { useEditConnector } from "../../hooks/useEditConnector";
import { useTestConnection } from "../../hooks/useTestConnection";
import { findCredentialsStep } from "../ConnectAccountDialog";
import { InlineErrorBanner } from "../InlineErrorBanner";
import { VendorBrandBadge } from "../VendorBrandBadge";

export interface EditConnectorDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The connection being edited. */
  readonly connection: ConnectionSummary;
  /** Same spec used at connect-time — drives the field schema. */
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  /**
   * Initial non-secret values to pre-populate. Caller usually passes
   * ``connection.metadata`` (BE projects host/port/db/username there).
   */
  readonly initialFields?: Readonly<Record<string, string>>;
}

function inputTypeFor(field: FieldSpec): React.HTMLInputTypeAttribute {
  switch (field.type) {
    case "password":
      return "password";
    case "email":
      return "email";
    case "url":
      return "url";
    case "number":
      return "number";
    default:
      return "text";
  }
}

function isSecretField(field: FieldSpec): boolean {
  return field.type === "password";
}

export const EditConnectorDialog: React.FC<EditConnectorDialogProps> = ({
  open,
  onOpenChange,
  connection,
  spec,
  initialFields,
}) => {
  const { i18n } = useLingui();
  const credentialsStep = React.useMemo(
    () => findCredentialsStep(spec, connection.vendor),
    [spec, connection.vendor],
  );

  // Seed the form: non-secret fields pre-filled from ``initialFields``,
  // secrets always blank. Recomputed every open so a fresh edit doesn't
  // inherit stale buffer state.
  const buildInitial = React.useCallback((): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const field of credentialsStep?.fields ?? []) {
      if (isSecretField(field)) {
        out[field.name] = "";
        continue;
      }
      out[field.name] = initialFields?.[field.name] ?? "";
    }
    return out;
  }, [credentialsStep, initialFields]);

  const [formValues, setFormValues] = React.useState<Record<string, string>>(buildInitial);
  const [error, setError] = React.useState<string | null>(null);
  const [testResult, setTestResult] = React.useState<ConnectionTestResponse | null>(null);
  // Per-field reveal state for password inputs. Keyed by field name so
  // a future spec that declares two password fields (e.g. PG + TLS
  // bundle passphrase) renders independent toggles. Set semantics: a
  // name's presence = the operator chose to reveal it. Reset whenever
  // the dialog re-opens so a fresh edit starts with everything masked
  // (no carry-over of "I revealed it last time").
  const [revealedSecrets, setRevealedSecrets] = React.useState<Set<string>>(() => new Set());

  React.useEffect(() => {
    if (!open) return;
    setFormValues(buildInitial());
    setError(null);
    setTestResult(null);
    setRevealedSecrets(new Set());
  }, [open, buildInitial]);

  const toggleReveal = React.useCallback((fieldName: string) => {
    setRevealedSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  }, []);

  const editMutation = useEditConnector();
  const testMutation = useTestConnection();

  const handleFieldChange = React.useCallback((name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const buildCredentialsPayload = React.useCallback(() => {
    // Strip blank secret fields so the BE preserves the existing value.
    const out: Record<string, string> = {};
    for (const field of credentialsStep?.fields ?? []) {
      const value = formValues[field.name] ?? "";
      if (isSecretField(field) && value.length === 0) continue;
      out[field.name] = value;
    }
    return out;
  }, [credentialsStep, formValues]);

  const handleTest = React.useCallback(() => {
    setError(null);
    setTestResult(null);
    // SOTA test-draft (June 10, 2026): forward the in-form credentials
    // payload so the BE probes the operator's pending values, not the
    // already-saved row. ``buildCredentialsPayload`` strips blank
    // secrets so the BE never receives an empty password and probes a
    // half-credential URL — instead the BE detects the blank and falls
    // back to "no credentials supplied = test saved" semantics if EVERY
    // field is empty (which only happens on a fresh open with no edits).
    const draft = buildCredentialsPayload();
    const hasDraft = Object.keys(draft).length > 0;
    testMutation.mutate(
      { connId: connection.conn_id, credentials: hasDraft ? draft : undefined },
      {
        onSuccess: (data) => {
          setTestResult(data);
          if (!data.success) {
            setError(data.error ?? i18n._(msg`Verifica fallita.`));
          }
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : i18n._(msg`Verifica non riuscita.`));
        },
      },
    );
  }, [buildCredentialsPayload, connection.conn_id, testMutation, i18n]);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      editMutation.mutate(
        {
          connId: connection.conn_id,
          capability: connection.capability,
          body: { credentials: buildCredentialsPayload() },
        },
        {
          onSuccess: () => onOpenChange(false),
          onError: (err) => {
            setError(err instanceof Error ? err.message : i18n._(msg`Salvataggio non riuscito.`));
          },
        },
      );
    },
    [
      buildCredentialsPayload,
      connection.capability,
      connection.conn_id,
      editMutation,
      onOpenChange,
      i18n,
    ],
  );

  const requiredMissing = React.useMemo(() => {
    if (!credentialsStep) return false;
    return (credentialsStep.fields ?? []).some((f) => {
      if (!f.required) return false;
      if (isSecretField(f)) return false; // secrets allowed blank on edit
      return (formValues[f.name] ?? "").trim().length === 0;
    });
  }, [credentialsStep, formValues]);

  const titleId = React.useId();
  const isPending = editMutation.isPending;

  return (
    <ConnectorDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._(msg`Modifica connessione`)}
      size="md"
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex min-w-0 items-start gap-3 pr-8">
        <VendorBrandBadge vendorId={connection.vendor} fallbackIcon={spec.icon} size="lg" />
        <div className="min-w-0">
          <h2
            id={titleId}
            className="font-display font-semibold text-[15px] text-ink-1 leading-tight"
          >
            <Trans>Modifica connessione</Trans>
          </h2>
          <p className="mt-1 truncate text-[12px] text-ink-2">
            {connection.connection_key ?? connection.conn_id}
          </p>
        </div>
      </div>

      {/* ── Test result chip ───────────────────────────────────── */}
      {testResult?.success ? (
        <output className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-[11.5px] text-success">
          <CheckCircle2 className="h-3 w-3" aria-hidden />
          <Trans>Connessione OK · {testResult.latency_ms}ms</Trans>
          {testResult.server_version ? <> · {testResult.server_version}</> : null}
        </output>
      ) : null}

      <InlineErrorBanner message={error} />

      {/* ── Form ───────────────────────────────────────────────── */}
      {credentialsStep ? (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3" aria-busy={isPending}>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleTest}
              disabled={testMutation.isPending || isPending}
            >
              {testMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : null}
              <Trans>Verifica connessione</Trans>
            </Button>
          </div>
          {(credentialsStep.fields ?? []).map((field) => {
            const fieldId = `ecd-${field.name}`;
            const isSecret = isSecretField(field);
            const isRevealed = isSecret && revealedSecrets.has(field.name);
            // When the operator reveals a password we swap the
            // input ``type`` from ``password`` → ``text`` so the
            // characters render in-place. The button stays a
            // sibling overlay so it doesn't grow the input's
            // intrinsic height (avoids a layout shift on toggle).
            const resolvedType: React.HTMLInputTypeAttribute = isRevealed
              ? "text"
              : inputTypeFor(field);
            return (
              <div key={field.name} className="flex flex-col gap-1">
                <label htmlFor={fieldId} className="font-medium text-[11.5px] text-ink-2">
                  {localize(field.label, i18n)}
                  {field.required === true && !isSecret ? (
                    <span className="ml-0.5 text-error" aria-hidden>
                      *
                    </span>
                  ) : null}
                </label>
                <div className="relative">
                  <input
                    id={fieldId}
                    name={field.name}
                    type={resolvedType}
                    placeholder={
                      isSecret
                        ? i18n._(msg`Lascia vuoto per non aggiornare`)
                        : localizeOpt(field.placeholder, i18n)
                    }
                    autoComplete={isSecret ? "new-password" : "off"}
                    data-1p-ignore={isSecret ? "true" : undefined}
                    value={formValues[field.name] ?? ""}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    disabled={isPending}
                    // Edge / IE add a native ``::-ms-reveal`` eye
                    // pseudo on password inputs that would collide
                    // visually with our custom toggle (two eye icons
                    // stacked). Inline style suppresses it without
                    // adding a global CSS rule for every other
                    // password field in the app.
                    style={isSecret ? ({ msReveal: "none" } as React.CSSProperties) : undefined}
                    className={cn(
                      "w-full rounded-md border border-hairline bg-white/60 px-2.5 py-1.5 text-[12.5px] text-ink-1",
                      "placeholder:text-ink-3",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                      "disabled:bg-ink-3/[0.04]",
                      isSecret && "pr-10",
                    )}
                  />
                  {isSecret ? (
                    <button
                      type="button"
                      onClick={() => toggleReveal(field.name)}
                      disabled={isPending}
                      // ``aria-label`` flips with state (Mostra ↔
                      // Nascondi). Per WAI-ARIA APG icon-toggle
                      // pattern, the dynamic label alone is the
                      // accessible name — adding ``aria-pressed``
                      // double-announces ("toggle premuto" + the
                      // already state-conveying label), so we omit
                      // it. Tested on VoiceOver + NVDA + JAWS.
                      aria-label={
                        isRevealed ? i18n._(msg`Nascondi password`) : i18n._(msg`Mostra password`)
                      }
                      className={cn(
                        "-translate-y-1/2 absolute top-1/2 right-1 inline-flex h-7 w-7",
                        "items-center justify-center rounded text-ink-3 transition-colors",
                        "hover:bg-black/[0.04] hover:text-ink-1",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                        "disabled:cursor-not-allowed disabled:opacity-40",
                      )}
                    >
                      {isRevealed ? (
                        <EyeOff className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <Eye className="h-3.5 w-3.5" aria-hidden />
                      )}
                    </button>
                  ) : null}
                </div>
                {field.helperText ? (
                  <span className="text-[11px] text-ink-3">{localize(field.helperText, i18n)}</span>
                ) : null}
              </div>
            );
          })}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={isPending || requiredMissing}
            >
              {isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              <Trans>Salva</Trans>
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-4 text-[12.5px] text-ink-3">
          <Trans>Questo connettore non espone campi modificabili.</Trans>
        </p>
      )}
    </ConnectorDialogShell>
  );
};
EditConnectorDialog.displayName = "EditConnectorDialog";
